import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import LoadingScreen from "@/components/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Mail, 
  MailOpen, 
  ArrowLeft, 
  RefreshCw, 
  Send,
  Flag,
  Circle,
  CheckCircle2,
  Reply,
  Plus,
  Inbox,
  SendHorizontal,
  FileEdit,
  Trash2,
  User
} from "lucide-react";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";

interface AdminEmail {
  id: string;
  from_email: string;
  to_email: string | null;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  received_at: string;
  is_read: boolean;
  is_flagged: boolean;
  replied_at: string | null;
  thread_id: string | null;
  in_reply_to: string | null;
}

interface SentEmail {
  id: string;
  to_email: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  sent_at: string;
  sent_by: string | null;
  in_reply_to: string | null;
}

// Unified thread message type for display
interface ThreadMessage {
  id: string;
  email: string; // from_email or to_email
  subject: string;
  body_html: string | null;
  body_text: string | null;
  date: string;
  type: "received" | "sent";
  is_read?: boolean;
  is_flagged?: boolean;
}

// Thread represents a conversation with multiple messages
interface EmailThread {
  thread_id: string;
  subject: string;
  latest_date: string;
  messages: ThreadMessage[];
  has_unread: boolean;
  is_flagged: boolean;
  primary_email: string; // The main correspondent
}

interface EmailDraft {
  id: string;
  to_email: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface DeletedEmail {
  id: string;
  original_id: string | null;
  to_email: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  deleted_at: string;
  deleted_by: string | null;
  original_type: string;
}

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

type FilterType = "all" | "unread" | "flagged" | "sent" | "drafts" | "deleted";

// Allowed sender addresses - must match edge function
const SENDER_OPTIONS = [
  { email: "team@anyskill.app", name: "AnySkill Team" },
  { email: "shane@anyskill.app", name: "Shane @ AnySkill" },
];

export default function AdminEmails() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  
  const [emails, setEmails] = useState<AdminEmail[]>([]);
  const [sentEmails, setSentEmails] = useState<SentEmail[]>([]);
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [deletedEmails, setDeletedEmails] = useState<DeletedEmail[]>([]);
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailDraft | DeletedEmail | null>(null);
  const [selectedSentEmail, setSelectedSentEmail] = useState<SentEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>("all");
  
  const [replySubject, setReplySubject] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);
  
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeToEmail, setComposeToEmail] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedSender, setSelectedSender] = useState(SENDER_OPTIONS[0].email);
  const [replySender, setReplySender] = useState(SENDER_OPTIONS[0].email);

  // Build threads from emails and sent emails
  const buildThreads = useCallback((inboxEmails: AdminEmail[], sentMails: SentEmail[]): EmailThread[] => {
    const threadMap = new Map<string, EmailThread>();
    
    // Group inbox emails by sender email address to keep all messages from same person together
    inboxEmails.forEach(email => {
      const threadKey = email.from_email.toLowerCase();
      const existing = threadMap.get(threadKey);
      
      const message: ThreadMessage = {
        id: email.id,
        email: email.from_email,
        subject: email.subject,
        body_html: email.body_html,
        body_text: email.body_text,
        date: email.received_at,
        type: "received",
        is_read: email.is_read,
        is_flagged: email.is_flagged,
      };
      
      if (existing) {
        existing.messages.push(message);
        if (new Date(email.received_at) > new Date(existing.latest_date)) {
          existing.latest_date = email.received_at;
          existing.subject = email.subject; // Update to latest subject
        }
        if (!email.is_read) existing.has_unread = true;
        if (email.is_flagged) existing.is_flagged = true;
      } else {
        threadMap.set(threadKey, {
          thread_id: threadKey,
          subject: email.subject,
          latest_date: email.received_at,
          messages: [message],
          has_unread: !email.is_read,
          is_flagged: email.is_flagged,
          primary_email: email.from_email,
        });
      }
    });
    
    // Process sent emails - add to thread based on recipient email
    sentMails.forEach(email => {
      const threadKey = email.to_email.toLowerCase();
      const existing = threadMap.get(threadKey);
      
      const message: ThreadMessage = {
        id: email.id,
        email: email.to_email,
        subject: email.subject,
        body_html: email.body_html,
        body_text: email.body_text,
        date: email.sent_at,
        type: "sent",
      };
      
      if (existing) {
        existing.messages.push(message);
        if (new Date(email.sent_at) > new Date(existing.latest_date)) {
          existing.latest_date = email.sent_at;
        }
      }
    });
    
    // Sort messages within each thread by date
    threadMap.forEach(thread => {
      thread.messages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });
    
    // Convert to array and sort by latest date
    return Array.from(threadMap.values()).sort(
      (a, b) => new Date(b.latest_date).getTime() - new Date(a.latest_date).getTime()
    );
  }, []);

  const threads = buildThreads(emails, sentEmails);

  const fetchEmails = useCallback(async () => {
    try {
      const [inboxRes, sentRes, draftsRes, deletedRes] = await Promise.all([
        supabase.from("admin_emails").select("*").order("received_at", { ascending: false }),
        supabase.from("sent_emails").select("*").order("sent_at", { ascending: false }),
        supabase.from("email_drafts").select("*").order("updated_at", { ascending: false }),
        supabase.from("deleted_emails").select("*").order("deleted_at", { ascending: false }),
      ]);

      if (inboxRes.error) throw inboxRes.error;
      if (sentRes.error) throw sentRes.error;
      if (draftsRes.error) throw draftsRes.error;
      if (deletedRes.error) throw deletedRes.error;

      setEmails(inboxRes.data || []);
      setSentEmails(sentRes.data || []);
      setDrafts(draftsRes.data || []);
      setDeletedEmails(deletedRes.data || []);
    } catch (error: any) {
      console.error("Error fetching emails:", error);
      toast.error("Failed to load emails");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .order("first_name");
      
      if (error) throw error;
      setUsers(data || []);

      const { data: usersData } = await supabase.functions.invoke("get-users");
      if (usersData?.users) {
        const emailMap: Record<string, string> = {};
        usersData.users.forEach((u: { id: string; email: string }) => {
          emailMap[u.id] = u.email;
        });
        setUserEmails(emailMap);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchEmails();
    fetchUsers();

    const channel = supabase
      .channel("admin-emails-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_emails" }, (payload) => {
        if (payload.eventType === "INSERT") {
          const newEmail = payload.new as AdminEmail;
          setEmails((prev) => [newEmail, ...prev]);
          toast.info(`New email from ${newEmail.from_email}`, { description: newEmail.subject });
        } else if (payload.eventType === "UPDATE") {
          const updated = payload.new as AdminEmail;
          setEmails((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
        } else if (payload.eventType === "DELETE") {
          setEmails((prev) => prev.filter((e) => e.id !== payload.old.id));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "sent_emails" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setSentEmails((prev) => [payload.new as SentEmail, ...prev]);
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "email_drafts" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setDrafts((prev) => [payload.new as EmailDraft, ...prev]);
        } else if (payload.eventType === "UPDATE") {
          setDrafts((prev) => prev.map((d) => (d.id === (payload.new as EmailDraft).id ? payload.new as EmailDraft : d)));
        } else if (payload.eventType === "DELETE") {
          setDrafts((prev) => prev.filter((d) => d.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, fetchEmails, fetchUsers]);

  if (roleLoading) return <LoadingScreen message="Verifying admin access..." />;
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <Navigate to="/admin-dashboard" replace />;

  const markThreadAsRead = async (thread: EmailThread) => {
    try {
      const unreadIds = thread.messages.filter(m => m.type === "received" && !m.is_read).map(m => m.id);
      if (unreadIds.length > 0) {
        await supabase.from("admin_emails").update({ is_read: true }).in("id", unreadIds);
      }
    } catch (error) {
      console.error("Error updating emails:", error);
    }
  };

  const toggleThreadFlag = async (thread: EmailThread) => {
    try {
      // Get the first received email in the thread to toggle
      const firstReceivedId = thread.messages.find(m => m.type === "received")?.id;
      if (firstReceivedId) {
        const email = emails.find(e => e.id === firstReceivedId);
        if (email) {
          await supabase.from("admin_emails").update({ is_flagged: !email.is_flagged }).eq("id", firstReceivedId);
        }
      }
    } catch (error) {
      console.error("Error toggling flag:", error);
      toast.error("Failed to update flag");
    }
  };

  const selectThread = (thread: EmailThread) => {
    setSelectedThread(thread);
    setSelectedEmail(null);
    setSelectedSentEmail(null);
    setShowReplyBox(false);
    setReplySubject(`Re: ${thread.subject}`);
    setReplyText("");
    if (thread.has_unread) markThreadAsRead(thread);
  };

  const selectDraftOrDeleted = (item: EmailDraft | DeletedEmail) => {
    setSelectedEmail(item);
    setSelectedThread(null);
    setSelectedSentEmail(null);
    setShowReplyBox(false);
  };

  const selectSentEmail = (email: SentEmail) => {
    setSelectedSentEmail(email);
    setSelectedThread(null);
    setSelectedEmail(null);
    setShowReplyBox(false);
  };

  // Trash a thread (moves all received emails to trash)
  const deleteThread = async (thread: EmailThread) => {
    try {
      const receivedMessages = thread.messages.filter(m => m.type === "received");
      const emailsToTrash = receivedMessages
        .map(msg => emails.find(e => e.id === msg.id))
        .filter((e): e is AdminEmail => e !== undefined);
      
      if (emailsToTrash.length === 0) {
        toast.info("No messages to trash");
        return;
      }
      
      // Insert all into deleted_emails first
      const deletedInserts = emailsToTrash.map(email => ({
        original_id: email.id,
        to_email: email.to_email || "admin@anyskill.com",
        subject: email.subject,
        body_text: email.body_text,
        body_html: email.body_html,
        deleted_by: user?.id,
        original_type: "received",
      }));
      
      const { error: insertError } = await supabase.from("deleted_emails").insert(deletedInserts);
      if (insertError) throw insertError;
      
      // Then delete all from admin_emails
      const idsToDelete = emailsToTrash.map(e => e.id);
      const { error: deleteError } = await supabase.from("admin_emails").delete().in("id", idsToDelete);
      if (deleteError) throw deleteError;
      
      // Update local state immediately
      setEmails(prev => prev.filter(e => !idsToDelete.includes(e.id)));
      setSelectedThread(null);
      
      // Refetch to sync deleted_emails list
      fetchEmails();
      toast.success("Thread moved to Trash");
    } catch (error) {
      console.error("Error trashing thread:", error);
      toast.error("Failed to trash thread");
    }
  };

  // Delete a single message from a thread
  const deleteMessage = async (message: ThreadMessage) => {
    try {
      if (message.type === "received") {
        const email = emails.find(e => e.id === message.id);
        if (email) {
          const { error: insertError } = await supabase.from("deleted_emails").insert({
            original_id: email.id,
            to_email: email.to_email || "admin@anyskill.com",
            subject: email.subject,
            body_text: email.body_text,
            body_html: email.body_html,
            deleted_by: user?.id,
            original_type: "received",
          });
          if (insertError) throw insertError;
          
          const { error: deleteError } = await supabase.from("admin_emails").delete().eq("id", email.id);
          if (deleteError) throw deleteError;
          
          // Update local state immediately
          setEmails(prev => prev.filter(e => e.id !== email.id));
        }
      } else {
        // Sent email
        const email = sentEmails.find(e => e.id === message.id);
        if (email) {
          const { error: insertError } = await supabase.from("deleted_emails").insert({
            original_id: email.id,
            to_email: email.to_email,
            subject: email.subject,
            body_text: email.body_text,
            body_html: email.body_html,
            deleted_by: user?.id,
            original_type: "sent",
          });
          if (insertError) throw insertError;
          
          const { error: deleteError } = await supabase.from("sent_emails").delete().eq("id", email.id);
          if (deleteError) throw deleteError;
          
          // Update local state immediately
          setSentEmails(prev => prev.filter(e => e.id !== email.id));
        }
      }
      
      fetchEmails();
      toast.success("Message moved to Trash");
    } catch (error) {
      console.error("Error trashing message:", error);
      toast.error("Failed to trash message");
    }
  };

  // Trash a sent email
  const deleteSentEmail = async (email: SentEmail) => {
    try {
      await supabase.from("deleted_emails").insert({
        original_id: email.id,
        to_email: email.to_email,
        subject: email.subject,
        body_text: email.body_text,
        body_html: email.body_html,
        deleted_by: user?.id,
        original_type: "sent",
      });
      await supabase.from("sent_emails").delete().eq("id", email.id);
      
      setSelectedSentEmail(null);
      toast.success("Email moved to Trash");
    } catch (error) {
      console.error("Error trashing sent email:", error);
      toast.error("Failed to trash email");
    }
  };

  // Permanently delete an email from trash
  const permanentlyDeleteEmail = async (deleted: DeletedEmail) => {
    try {
      await supabase.from("deleted_emails").delete().eq("id", deleted.id);
      setSelectedEmail(null);
      fetchEmails();
      toast.success("Email permanently deleted");
    } catch (error) {
      console.error("Error permanently deleting email:", error);
      toast.error("Failed to permanently delete email");
    }
  };

  const formatEmailBodyHtml = (text: string) =>
    text
      .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
      .replace(/\n/g, "<br>");

  const sendReply = async () => {
    if (!selectedThread || !replyText.trim() || !replySubject.trim()) return;

    setSending(true);
    try {
      // Get the latest received message in the thread
      const latestReceived = [...selectedThread.messages].reverse().find(m => m.type === "received");
      if (!latestReceived) return;

      const originalContent = latestReceived.body_html || latestReceived.body_text || "(No original content)";

      const { error } = await supabase.functions.invoke("send-admin-reply", {
        body: {
          to: selectedThread.primary_email,
          subject: replySubject,
          html: `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="padding: 20px;">${formatEmailBodyHtml(replyText)}</div>
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #e5e7eb;">
              <div style="padding: 0 20px 20px;">
                <p style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">
                  On ${new Date(latestReceived.date).toLocaleString()}, ${latestReceived.email} wrote:
                </p>
                <blockquote style="margin: 0; padding: 12px 16px; border-left: 3px solid #d1d5db; background: #f9fafb; color: #4b5563;">
                  ${originalContent}
                </blockquote>
              </div>
            </div>
          `,
          email_id: latestReceived.id,
          from_email: replySender,
        },
      });

      if (error) throw error;

      await supabase.from("sent_emails").insert({
        to_email: selectedThread.primary_email,
        subject: replySubject,
        body_text: replyText,
        body_html: `<div>${formatEmailBodyHtml(replyText)}</div>`,
        sent_by: user?.id,
        in_reply_to: latestReceived.id,
      });

      setReplyText("");
      setShowReplyBox(false);
      toast.success("Reply sent");
    } catch (error: any) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const sendNewEmail = async () => {
    if (!composeToEmail.trim() || !composeSubject.trim() || !composeBody.trim()) return;

    setComposeSending(true);
    try {
      const senderInfo = SENDER_OPTIONS.find(s => s.email === selectedSender) || SENDER_OPTIONS[0];
      const { error } = await supabase.functions.invoke("send-admin-reply", {
        body: {
          to: composeToEmail,
          subject: composeSubject,
          html: `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="padding: 20px;">${formatEmailBodyHtml(composeBody)}</div>
              <div style="padding: 20px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; color: #9ca3af; font-size: 12px;">&copy; ${new Date().getFullYear()} ${senderInfo.name}</p>
              </div>
            </div>
          `,
          from_email: selectedSender,
        },
      });

      if (error) throw error;

      await supabase.from("sent_emails").insert({
        to_email: composeToEmail,
        subject: composeSubject,
        body_text: composeBody,
        body_html: `<div>${formatEmailBodyHtml(composeBody)}</div>`,
        sent_by: user?.id,
      });

      // If editing a draft, delete it from drafts after sending
      if (editingDraftId) {
        await supabase.from("email_drafts").delete().eq("id", editingDraftId);
        setEditingDraftId(null);
      }

      setComposeToEmail("");
      setComposeSubject("");
      setComposeBody("");
      setComposeOpen(false);
      toast.success("Email sent");
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setComposeSending(false);
    }
  };

  const saveDraft = async () => {
    if (!composeToEmail.trim() && !composeSubject.trim() && !composeBody.trim()) {
      clearComposeState();
      return;
    }

    try {
      if (editingDraftId) {
        // Update existing draft
        await supabase.from("email_drafts").update({
          to_email: composeToEmail || "(No recipient)",
          subject: composeSubject || "(No subject)",
          body_text: composeBody,
          body_html: composeBody ? `<div>${formatEmailBodyHtml(composeBody)}</div>` : null,
        }).eq("id", editingDraftId);
      } else {
        // Create new draft
        await supabase.from("email_drafts").insert({
          to_email: composeToEmail || "(No recipient)",
          subject: composeSubject || "(No subject)",
          body_text: composeBody,
          body_html: composeBody ? `<div>${formatEmailBodyHtml(composeBody)}</div>` : null,
          created_by: user?.id,
        });
      }

      clearComposeState();
      toast.success("Draft saved");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    }
  };

  const deleteComposeContent = async () => {
    const hasContent = composeToEmail.trim() || composeSubject.trim() || composeBody.trim();

    if (hasContent) {
      // Move to deleted emails
      await supabase.from("deleted_emails").insert({
        original_id: editingDraftId,
        to_email: composeToEmail || "(No recipient)",
        subject: composeSubject || "(No subject)",
        body_text: composeBody,
        body_html: composeBody ? `<div>${formatEmailBodyHtml(composeBody)}</div>` : null,
        deleted_by: user?.id,
        original_type: editingDraftId ? "draft" : "compose",
      });

      // If editing a draft, delete from drafts table
      if (editingDraftId) {
        await supabase.from("email_drafts").delete().eq("id", editingDraftId);
      }
    }

    clearComposeState();
    toast.success("Moved to Trash");
  };

  const clearComposeState = () => {
    setComposeToEmail("");
    setComposeSubject("");
    setComposeBody("");
    setComposeOpen(false);
    setEditingDraftId(null);
    setCloseDialogOpen(false);
  };

  const handleComposeClose = () => {
    const hasContent = composeToEmail.trim() || composeSubject.trim() || composeBody.trim();
    if (hasContent) {
      setCloseDialogOpen(true);
    } else {
      clearComposeState();
    }
  };

  const deleteDraft = async (draftId: string, draft: EmailDraft) => {
    try {
      // Move to deleted emails first
      await supabase.from("deleted_emails").insert({
        original_id: draftId,
        to_email: draft.to_email,
        subject: draft.subject,
        body_text: draft.body_text,
        body_html: draft.body_html,
        deleted_by: user?.id,
        original_type: "draft",
      });
      
      await supabase.from("email_drafts").delete().eq("id", draftId);
      setSelectedEmail(null);
      toast.success("Moved to Trash");
    } catch (error) {
      console.error("Error trashing draft:", error);
      toast.error("Failed to trash draft");
    }
  };

  const openDraftForEditing = (draft: EmailDraft) => {
    setComposeToEmail(draft.to_email === "(No recipient)" ? "" : draft.to_email);
    setComposeSubject(draft.subject === "(No subject)" ? "" : draft.subject);
    setComposeBody(draft.body_text || "");
    setEditingDraftId(draft.id);
    setComposeOpen(true);
    setSelectedEmail(null);
  };

  const formatEmailDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return format(date, "h:mm a");
    if (isYesterday(date)) return "Yesterday";
    return format(date, "MMM d");
  };

  const getSenderName = (email: string) => {
    if (!email || email === "(No recipient)") return "Unknown";
    const name = email.split("@")[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const getSenderInitials = (email: string) => {
    if (!email || email === "(No recipient)") return "?";
    const name = email.split("@")[0];
    return name.substring(0, 2).toUpperCase();
  };

  const getFilteredThreads = (): EmailThread[] => {
    switch (filter) {
      case "unread": return threads.filter((t) => t.has_unread);
      case "flagged": return threads.filter((t) => t.is_flagged);
      default: return threads;
    }
  };

  // Build sent-only items for the Sent mailbox
  const sentItems = sentEmails.map(email => ({
    id: email.id,
    to_email: email.to_email,
    subject: email.subject,
    body_html: email.body_html,
    body_text: email.body_text,
    sent_at: email.sent_at,
    sent_by: email.sent_by,
    in_reply_to: email.in_reply_to,
  }));

  const unreadCount = threads.filter((t) => t.has_unread).length;
  const flaggedCount = threads.filter((t) => t.is_flagged).length;
  const filteredThreads = getFilteredThreads();

  const isDraft = (item: any): item is EmailDraft => "created_by" in item && "updated_at" in item && !("deleted_at" in item);
  const isDeletedEmail = (item: any): item is DeletedEmail => "deleted_at" in item;

  const getEmailDate = (item: EmailDraft | DeletedEmail): string => {
    if (isDraft(item)) return item.updated_at;
    if (isDeletedEmail(item)) return item.deleted_at;
    return new Date().toISOString();
  };

  return (
    <TooltipProvider>
      <div className="h-screen flex flex-col bg-background">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin-dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">Emails</h1>
              {unreadCount > 0 && (
                <Badge variant="default" className="rounded-full px-2 py-0.5 text-xs">
                  {unreadCount}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={composeOpen} onOpenChange={(open) => {
              if (!open) {
                handleComposeClose();
              } else {
                setComposeOpen(true);
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Compose
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg" onInteractOutside={(e) => {
                e.preventDefault();
                handleComposeClose();
              }}>
                <DialogHeader>
                  <DialogTitle>{editingDraftId ? "Edit Draft" : "New Email"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>From</Label>
                    <Select value={selectedSender} onValueChange={setSelectedSender}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SENDER_OPTIONS.map((sender) => (
                          <SelectItem key={sender.email} value={sender.email}>
                            {sender.name} &lt;{sender.email}&gt;
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>To</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="recipient@email.com"
                        value={composeToEmail}
                        onChange={(e) => setComposeToEmail(e.target.value)}
                        className="flex-1"
                      />
                      <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="icon" className="shrink-0">
                            <User className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="end">
                          <Command>
                            <CommandInput placeholder="Search users..." />
                            <CommandList>
                              <CommandEmpty>No users found.</CommandEmpty>
                              <CommandGroup>
                                {users.map((u) => {
                                  const email = userEmails[u.id];
                                  if (!email) return null;
                                  return (
                                    <CommandItem
                                      key={u.id}
                                      value={`${u.first_name} ${u.last_name} ${email}`}
                                      onSelect={() => {
                                        setComposeToEmail(email);
                                        setUserSearchOpen(false);
                                      }}
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium">
                                          {u.first_name || ""} {u.last_name || ""}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{email}</span>
                                      </div>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      placeholder="Subject"
                      value={composeSubject}
                      onChange={(e) => setComposeSubject(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      placeholder="Write your message..."
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Tab") {
                          e.preventDefault();
                          const target = e.target as HTMLTextAreaElement;
                          const start = target.selectionStart;
                          const end = target.selectionEnd;
                          const newValue = composeBody.substring(0, start) + "\t" + composeBody.substring(end);
                          setComposeBody(newValue);
                          // Set cursor position after the tab
                          setTimeout(() => {
                            target.selectionStart = target.selectionEnd = start + 1;
                          }, 0);
                        }
                      }}
                      rows={8}
                      className="resize-none"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={sendNewEmail} disabled={!composeToEmail.trim() || !composeSubject.trim() || !composeBody.trim() || composeSending}>
                      <Send className="h-4 w-4 mr-2" />
                      {composeSending ? "Sending..." : "Send"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="ghost" size="icon" onClick={fetchEmails} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-48 border-r bg-muted/30 p-3 hidden md:block">
            <nav className="space-y-1">
              {[
                { key: "all" as FilterType, icon: Inbox, label: "All Mail", count: emails.length },
                { key: "unread" as FilterType, icon: Circle, label: "Unread", count: unreadCount },
                { key: "flagged" as FilterType, icon: Flag, label: "Flagged", count: flaggedCount },
                { key: "sent" as FilterType, icon: SendHorizontal, label: "Sent", count: sentEmails.length },
                { key: "drafts" as FilterType, icon: FileEdit, label: "Drafts", count: drafts.length },
                { key: "deleted" as FilterType, icon: Trash2, label: "Trash", count: deletedEmails.length },
              ].map(({ key, icon: Icon, label, count }) => (
                <button
                  key={key}
                  onClick={() => { setFilter(key); setSelectedEmail(null); setSelectedThread(null); setSelectedSentEmail(null); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                    filter === key
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {count > 0 && <span className="ml-auto text-xs opacity-70">{count}</span>}
                </button>
              ))}
            </nav>
          </div>

          <div className="w-full md:w-80 lg:w-96 border-r flex flex-col">
            <div className="flex border-b md:hidden overflow-x-auto">
              {[
                { key: "all", label: "All" },
                { key: "unread", label: "Unread" },
                { key: "flagged", label: "Flagged" },
                { key: "sent", label: "Sent" },
                { key: "drafts", label: "Drafts" },
                { key: "deleted", label: "Trash" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setFilter(key as FilterType); setSelectedEmail(null); setSelectedThread(null); setSelectedSentEmail(null); }}
                  className={`flex-1 py-2 text-xs font-medium whitespace-nowrap px-2 ${
                    filter === key ? "border-b-2 border-primary text-primary" : "text-muted-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <ScrollArea className="flex-1">
              {loading ? (
                <div className="p-3 space-y-2">
                  {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
                </div>
              ) : filter === "drafts" ? (
                drafts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <FileEdit className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No drafts</p>
                  </div>
                ) : (
                  <div>
                    {drafts.map((draft) => (
                      <div
                        key={draft.id}
                        onClick={() => selectDraftOrDeleted(draft)}
                        className={`group relative cursor-pointer border-b transition-colors ${
                          selectedEmail?.id === draft.id
                            ? "bg-primary/10 border-l-2 border-l-primary"
                            : "hover:bg-muted/50 border-l-2 border-l-transparent"
                        }`}
                      >
                        <div className="flex items-start gap-3 p-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium bg-muted text-muted-foreground">
                            {getSenderInitials(draft.to_email)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm truncate text-muted-foreground">
                                To: {getSenderName(draft.to_email)}
                              </span>
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatEmailDate(draft.updated_at)}
                              </span>
                            </div>
                            <p className="text-sm truncate mt-0.5 text-muted-foreground">
                              {draft.subject || "(No subject)"}
                            </p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {draft.body_text?.slice(0, 80) || "No preview"}
                            </p>
                          </div>
                          <FileEdit className="h-3.5 w-3.5 text-muted-foreground/50" />
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : filter === "deleted" ? (
                deletedEmails.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Trash2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No deleted emails</p>
                  </div>
                ) : (
                  <div>
                    {deletedEmails.map((deleted) => (
                      <div
                        key={deleted.id}
                        onClick={() => selectDraftOrDeleted(deleted)}
                        className={`group relative cursor-pointer border-b transition-colors ${
                          selectedEmail?.id === deleted.id
                            ? "bg-primary/10 border-l-2 border-l-primary"
                            : "hover:bg-muted/50 border-l-2 border-l-transparent"
                        }`}
                      >
                        <div className="flex items-start gap-3 p-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium bg-muted text-muted-foreground">
                            {getSenderInitials(deleted.to_email)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm truncate text-muted-foreground">
                                To: {getSenderName(deleted.to_email)}
                              </span>
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatEmailDate(deleted.deleted_at)}
                              </span>
                            </div>
                            <p className="text-sm truncate mt-0.5 text-muted-foreground">
                              {deleted.subject || "(No subject)"}
                            </p>
                          </div>
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground/50" />
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : filter === "sent" ? (
                sentItems.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <SendHorizontal className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No sent emails</p>
                  </div>
                ) : (
                  <div>
                    {sentItems.map((email) => (
                      <div
                        key={email.id}
                        onClick={() => selectSentEmail(email)}
                        className={`group relative cursor-pointer border-b transition-colors ${
                          selectedSentEmail?.id === email.id
                            ? "bg-primary/10 border-l-2 border-l-primary"
                            : "hover:bg-muted/50 border-l-2 border-l-transparent"
                        }`}
                      >
                        <div className="flex items-start gap-3 p-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium bg-primary text-primary-foreground">
                            {getSenderInitials(email.to_email)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm truncate text-muted-foreground">
                                To: {getSenderName(email.to_email)}
                              </span>
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatEmailDate(email.sent_at)}
                              </span>
                            </div>
                            <p className="text-sm truncate mt-0.5 text-foreground">
                              {email.subject || "(No subject)"}
                            </p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {email.body_text?.slice(0, 80) || "No preview"}
                            </p>
                          </div>
                          <SendHorizontal className="h-3.5 w-3.5 text-muted-foreground/50" />
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : filteredThreads.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Mail className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No emails</p>
                </div>
              ) : (
                <div>
                  {filteredThreads.map((thread) => {
                    const latestMessage = thread.messages[thread.messages.length - 1];
                    
                    return (
                      <div
                        key={thread.thread_id}
                        onClick={() => selectThread(thread)}
                        className={`group relative cursor-pointer border-b transition-colors ${
                          selectedThread?.thread_id === thread.thread_id
                            ? "bg-primary/10 border-l-2 border-l-primary"
                            : "hover:bg-muted/50 border-l-2 border-l-transparent"
                        }`}
                      >
                        <div className="flex items-start gap-3 p-3">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium ${
                            thread.has_unread ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          }`}>
                            {getSenderInitials(thread.primary_email)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm truncate ${thread.has_unread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                                  {getSenderName(thread.primary_email)}
                                </span>
                                {thread.messages.length > 1 && (
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                    {thread.messages.length}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatEmailDate(thread.latest_date)}
                              </span>
                            </div>
                            <p className={`text-sm truncate mt-0.5 ${thread.has_unread ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                              {thread.subject || "(No subject)"}
                            </p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {latestMessage?.body_text?.slice(0, 80) || "No preview"}
                            </p>
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            {thread.has_unread && <div className="w-2 h-2 rounded-full bg-primary" />}
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleThreadFlag(thread); }}
                              className={`p-1 rounded transition-colors ${
                                thread.is_flagged ? "text-orange-500" : "text-muted-foreground/30 hover:text-muted-foreground opacity-0 group-hover:opacity-100"
                              }`}
                            >
                              <Flag className="h-3.5 w-3.5" fill={thread.is_flagged ? "currentColor" : "none"} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          <div className="flex-1 flex flex-col bg-card">
            {selectedThread ? (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-semibold truncate">{selectedThread.subject || "(No subject)"}</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedThread.messages.length} message{selectedThread.messages.length !== 1 ? "s" : ""} with {getSenderName(selectedThread.primary_email)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => toggleThreadFlag(selectedThread)}>
                            <Flag className={`h-4 w-4 ${selectedThread.is_flagged ? "text-orange-500" : ""}`} fill={selectedThread.is_flagged ? "currentColor" : "none"} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{selectedThread.is_flagged ? "Unflag" : "Flag"}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => deleteThread(selectedThread)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Trash Thread</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                <ScrollArea className="flex-1">
                  <div className="p-4 space-y-4">
                    {selectedThread.messages.map((message, index) => (
                      <div
                        key={message.id}
                        className={`group rounded-lg border p-4 ${
                          message.type === "sent" ? "bg-primary/5 border-primary/20 ml-8" : "bg-card"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                            message.type === "sent" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          }`}>
                            {message.type === "sent" ? "Me" : getSenderInitials(message.email)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className="font-medium text-sm">
                                {message.type === "sent" ? "You" : getSenderName(message.email)}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(message.date), "MMM d, yyyy 'at' h:mm a")}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => { e.stopPropagation(); deleteMessage(message); }}
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              </div>
                            </div>
                            {message.body_html && message.body_html.trim() !== "" ? (
                              <div className="prose prose-sm max-w-none dark:prose-invert text-sm email-content" dangerouslySetInnerHTML={{ __html: message.body_html }} />
                            ) : message.body_text && message.body_text.trim() !== "" ? (
                              <div className="text-sm leading-relaxed whitespace-pre-wrap">{message.body_text}</div>
                            ) : (
                              <p className="text-muted-foreground italic text-sm">No content available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                <div className="border-t">
                  {!showReplyBox ? (
                    <div className="p-4">
                      <Button onClick={() => setShowReplyBox(true)} className="w-full" variant="outline">
                        <Reply className="h-4 w-4 mr-2" />
                        Reply to {getSenderName(selectedThread.primary_email)}
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Reply</Label>
                        <Button variant="ghost" size="sm" onClick={() => setShowReplyBox(false)}>Cancel</Button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">From:</span>
                          <Select value={replySender} onValueChange={setReplySender}>
                            <SelectTrigger className="h-8 w-auto bg-background">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SENDER_OPTIONS.map((sender) => (
                                <SelectItem key={sender.email} value={sender.email}>
                                  {sender.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">To:</span>
                          <span>{selectedThread.primary_email}</span>
                        </div>
                        <Input placeholder="Subject" value={replySubject} onChange={(e) => setReplySubject(e.target.value)} className="bg-background" />
                      </div>
                      <Textarea placeholder="Write your reply..." value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={5} className="resize-none bg-background" />
                      <div className="flex justify-end">
                        <Button onClick={sendReply} disabled={!replyText.trim() || !replySubject.trim() || sending}>
                          <Send className="h-4 w-4 mr-2" />
                          {sending ? "Sending..." : "Send Reply"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : selectedEmail ? (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-semibold truncate">{selectedEmail.subject || "(No subject)"}</h2>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                          {getSenderInitials(selectedEmail.to_email)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">To: {getSenderName(selectedEmail.to_email)}</span>
                            <span className="text-sm text-muted-foreground">&lt;{selectedEmail.to_email}&gt;</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(getEmailDate(selectedEmail)), "EEEE, MMMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isDraft(selectedEmail) && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openDraftForEditing(selectedEmail)}>
                            <FileEdit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => deleteDraft(selectedEmail.id, selectedEmail)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Trash</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                      {isDeletedEmail(selectedEmail) && (
                        <Button variant="destructive" size="sm" onClick={() => permanentlyDeleteEmail(selectedEmail)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Permanently Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <ScrollArea className="flex-1">
                  <div className="p-6">
                    {selectedEmail.body_html && selectedEmail.body_html.trim() !== "" ? (
                      <div className="prose prose-sm max-w-none dark:prose-invert email-content" dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
                    ) : selectedEmail.body_text && selectedEmail.body_text.trim() !== "" ? (
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">{selectedEmail.body_text}</div>
                    ) : (
                      <p className="text-muted-foreground italic">No content available</p>
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : selectedSentEmail ? (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-semibold truncate">{selectedSentEmail.subject || "(No subject)"}</h2>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-medium text-primary-foreground">
                          {getSenderInitials(selectedSentEmail.to_email)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">To: {getSenderName(selectedSentEmail.to_email)}</span>
                            <span className="text-sm text-muted-foreground">&lt;{selectedSentEmail.to_email}&gt;</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Sent on {format(new Date(selectedSentEmail.sent_at), "EEEE, MMMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => deleteSentEmail(selectedSentEmail)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>

                <ScrollArea className="flex-1">
                  <div className="p-6">
                    {selectedSentEmail.body_html && selectedSentEmail.body_html.trim() !== "" ? (
                      <div className="prose prose-sm max-w-none dark:prose-invert email-content" dangerouslySetInnerHTML={{ __html: selectedSentEmail.body_html }} />
                    ) : selectedSentEmail.body_text && selectedSentEmail.body_text.trim() !== "" ? (
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">{selectedSentEmail.body_text}</div>
                    ) : (
                      <p className="text-muted-foreground italic">No content available</p>
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-10 w-10 opacity-30" />
                  </div>
                  <p className="text-lg font-medium">Select an email</p>
                  <p className="text-sm">Choose from the list to read</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <AlertDialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save or Delete?</AlertDialogTitle>
              <AlertDialogDescription>
                What would you like to do with this email?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setCloseDialogOpen(false)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteComposeContent} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </AlertDialogAction>
              <AlertDialogAction onClick={saveDraft}>
                <FileEdit className="h-4 w-4 mr-2" />
                Save Draft
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
